import React, { useCallback } from "react";

import Select, { RefSelectProps } from "antd/es/select";
import cn from "classnames";
import { capitalize } from "lodash-es";
import { useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import RecordSource from "@mapmycustomers/shared/enum/RecordSource";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import {
  IFilterComponentProps,
  IFilterInstance,
} from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import { LegacySimpleCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import { getRecordSourceTypeDisplayName } from "@app/util/ui";

import getHumanReadableDescriptionForOptionsFilter, {
  OptionFilterOperator,
} from "../getHumanReadableDescriptionForOptionsFilter";

import styles from "./SourceFilter.module.scss";

export const SOURCE_FILTER_OPERATORS = [FilterOperator.IN_ANY, FilterOperator.NONE_OF];

const AllRecordSourceType = Object.values(RecordSource);

const doesSupportValue = (value: any, operator: FilterOperator) =>
  Array.isArray(value) &&
  value.every(
    (item) => typeof item === "string" && AllRecordSourceType.includes(item as RecordSource)
  ) &&
  SOURCE_FILTER_OPERATORS.includes(operator);

interface SourceFilterProps extends IFilterComponentProps {}

const SourceFilter: IFilterInstance = {
  doesSupportValue,
  getComponent:
    (): React.FunctionComponent<SourceFilterProps> =>
    ({ className, disabled, focus, onChange, options, placeholder, value }) => {
      const intl = useIntl();
      const availableOptions = (options?.availableSources ?? [
        ...AllRecordSourceType,
      ]) as RecordSource[];

      const setRef = useCallback(
        (ref: null | RefSelectProps) => {
          if (focus && ref) {
            ref.focus();
          }
        },
        [focus]
      );

      return (
        <Select<RecordSource[]>
          className={cn(styles.container, className)}
          disabled={disabled}
          filterOption={(inputValue, option) =>
            ((option?.label ?? option?.value) as string) // children because display names are different than value
              ?.toLowerCase()
              .includes(inputValue.toLowerCase().trim()) ?? false
          }
          mode="multiple"
          onChange={(types: RecordSource[]) => onChange?.({ ...value, value: types })}
          options={availableOptions.map((type) => ({
            label: getRecordSourceTypeDisplayName(intl, type),
            value: type,
          }))}
          placeholder={
            placeholder ??
            intl.formatMessage({
              id: "filters.source.select.placeholder",
              defaultMessage: "Click or type to select source",
              description: "Placeholder displayed in SourceFilter's select field",
            })
          }
          ref={setRef}
          showSearch
          value={Array.isArray(value.value) ? value.value : []}
        />
      );
    },
  getHumanReadableDescription: (value: LegacySimpleCondition, field: IField) =>
    doesSupportValue(value.value, value.operator)
      ? getHumanReadableDescriptionForOptionsFilter(
          field,
          value.operator as OptionFilterOperator,
          value.value.map(capitalize)
        )
      : undefined,
};

export default SourceFilter;
